import React, { useCallback, useState } from 'react';
import type { showErrorFlag, showSuccessFlag } from '@helpcenter/flags';
import { getCloudId } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/button';
import LoadingButton from '@atlaskit/button/loading-button';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import Toggle from '@atlaskit/toggle/toggle';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import {
	isFetchNetworkOrClientError,
	type FetchError,
} from '@atlassian/help-center-common-util/fetch';
import { useHelpCenterType } from '../../../contexts/help-center-type-context';
import { useHelpCenterAri } from '../../../hooks/use-help-center-ari';
import type { syncProjectsToggleFragment$key } from './__generated__/syncProjectsToggleFragment.graphql';
import type {
	syncProjectsToggleMutation,
	syncProjectsToggleMutation$data,
} from './__generated__/syncProjectsToggleMutation.graphql';
import messages from './messages';

export interface Props {
	showErrorFlag: typeof showErrorFlag;
	showSuccessFlag: typeof showSuccessFlag;
	syncProjectsToggleFragmentKey: syncProjectsToggleFragment$key;
}

const SyncProjectsToggle = ({
	syncProjectsToggleFragmentKey,
	showErrorFlag: showErrorFlagAction,
	showSuccessFlag: showSuccessFlagAction,
}: Props) => {
	di(useMutation, getCloudId, useFragment, useHelpCenterAri, useHelpCenterType);
	const helpCenterData = useFragment(
		graphql`
			fragment syncProjectsToggleFragment on HelpCenter {
				projectMappingData {
					syncNewProjects
				}
			}
		`,
		syncProjectsToggleFragmentKey,
	);
	const [isChecked, setIsChecked] = useState(!!helpCenterData?.projectMappingData?.syncNewProjects);
	const [isRemoveMappingDialogOpen, setRemoveMappingDialogOpen] = useState(false);
	const [isUnSyncing, setIsUnSyncing] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const helpCenterAri = useHelpCenterAri();
	const { isHelpHub } = useHelpCenterType();
	const isProjectRenamingEnabled = FeatureGates.checkGate('itsm-project-renaming');

	let toggleLabel;
	if (isHelpHub && isProjectRenamingEnabled) {
		toggleLabel = messages.defaultMappingToggleLabelGalaxiaHelpHub;
	} else if (isHelpHub) {
		toggleLabel = messages.defaultMappingToggleLabelHelpHub;
	} else if (isProjectRenamingEnabled) {
		toggleLabel = messages.defaultMappingToggleLabelGalaxia;
	} else {
		toggleLabel = messages.defaultMappingToggleLabel;
	}

	const [toggleDefaultSync] = useMutation<syncProjectsToggleMutation>(graphql`
		mutation syncProjectsToggleMutation(
			$cloudId: ID!
			$input: HelpCenterProjectMappingUpdateInput!
		) {
			helpCenter(cloudId: $cloudId) {
				updateProjectMapping(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);
	const onToggleDefault = useCallback(
		(syncNewProjects: boolean) => {
			return new Promise<syncProjectsToggleMutation$data>((resolve, reject) =>
				toggleDefaultSync({
					variables: {
						cloudId: getCloudId(),
						input: {
							syncNewProjects,
							helpCenterAri,
						},
					},
					onCompleted: (response) => {
						if (response.helpCenter?.updateProjectMapping?.success) {
							resolve(response);
						}
						const errors = response.helpCenter?.updateProjectMapping?.errors;
						const error = errors && errors.length > 0 ? errors[0] : new Error('Unknown error');
						return reject(error);
					},
					onError: (error) => {
						reject(error);
					},
				}),
			);
		},
		[toggleDefaultSync, helpCenterAri],
	);

	const onToggleChange = useCallback(() => {
		if (isChecked) {
			setRemoveMappingDialogOpen(true);
		} else {
			setIsChecked(true);
			onToggleDefault(true)
				.then(() => {
					createAnalyticsEvent({
						analyticsType: TRACK_EVENT_TYPE,
						action: 'map',
						actionSubject: 'newProjects',
						toggleState: true,
					}).fire();
				})
				.catch((error: FetchError) => {
					const { message, status } = error;
					setIsChecked(false);
					const errorTitle = isProjectRenamingEnabled
						? messages.syncErrorTitleGalaxia
						: messages.syncErrorTitle;
					showErrorFlagAction(errorTitle, messages.syncErrorDescription);
					if (!isFetchNetworkOrClientError(error)) {
						createAnalyticsEvent({
							analyticsType: TRACK_EVENT_TYPE,
							action: 'map_failed',
							actionSubject: 'newProjects',
							errorMessage: message,
							errorStatus: status,
						}).fire();
					}
				});
		}
	}, [
		isChecked,
		onToggleDefault,
		showErrorFlagAction,
		createAnalyticsEvent,
		isProjectRenamingEnabled,
	]);

	const onUnMapNewProjects = useCallback(() => {
		setIsUnSyncing(true);
		onToggleDefault(false)
			.then(() => {
				setIsChecked(false);
				const successTitle = isProjectRenamingEnabled
					? messages.unSyncSuccessTitleGalaxia
					: messages.unSyncSuccessTitle;
				showSuccessFlagAction(successTitle);
				createAnalyticsEvent({
					analyticsType: TRACK_EVENT_TYPE,
					action: 'map',
					actionSubject: 'newProjects',
					toggleState: false,
				}).fire();
			})
			.catch((error: FetchError) => {
				const { message, status } = error;
				const errorTitle = isProjectRenamingEnabled
					? messages.syncErrorTitleGalaxia
					: messages.syncErrorTitle;
				showErrorFlagAction(errorTitle, messages.syncErrorDescription);
				if (!isFetchNetworkOrClientError(error)) {
					createAnalyticsEvent({
						analyticsType: TRACK_EVENT_TYPE,
						action: 'map_failed',
						actionSubject: 'newProjects',
						errorMessage: message,
						errorStatus: status,
					}).fire();
				}
			})
			.finally(() => {
				setIsUnSyncing(false);
				setRemoveMappingDialogOpen(false);
			});
	}, [
		onToggleDefault,
		showErrorFlagAction,
		showSuccessFlagAction,
		createAnalyticsEvent,
		isProjectRenamingEnabled,
	]);

	const modalTitle = isProjectRenamingEnabled
		? messages.removeMappingModalTitleGalaxia
		: messages.removeMappingModalTitle;
	const modalDesc = isProjectRenamingEnabled
		? messages.removeMappingModalDescGalaxia
		: messages.removeMappingModalDesc;

	return (
		<>
			<Toggle label={formatMessage(toggleLabel)} isChecked={isChecked} onChange={onToggleChange} />
			<ModalTransition>
				{isRemoveMappingDialogOpen && (
					<Modal width="small" onClose={() => setRemoveMappingDialogOpen(false)}>
						<ModalHeader>
							<ModalTitle testId="help-center-UnSync-modal-title">
								{formatMessage(modalTitle)}
							</ModalTitle>
						</ModalHeader>
						<ModalBody>{formatMessage(modalDesc)}</ModalBody>
						<ModalFooter>
							<LoadingButton
								onClick={onUnMapNewProjects}
								isLoading={isUnSyncing}
								appearance="primary"
								testId="help-center-modal-unSync-button"
							>
								{formatMessage(messages.removeMappingModalConfirmButton)}
							</LoadingButton>
							<Button onClick={() => setRemoveMappingDialogOpen(false)} appearance="subtle">
								{formatMessage(messages.removeMappingModalCancelButton)}
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};

export default SyncProjectsToggle;
